import { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUIStore } from './stores/uiStore';
import { useCalculationStore } from './stores/calculationStore';
import { useProductionStore } from './stores/productionStore';
import { usePricingStore } from './stores/pricingStore';
import { useExpenseStore } from './stores/expenseStore';
import { useCycleStore } from './stores/cycleStore';
import { Header, Sidebar, MainContent, ResultsPanel, FCRTable, ManualFCRInput, Footer } from './components/layout';
import { Card, Button, SEOHead, StructuredData } from './components/common';
import { PerformanceMonitor } from './components/common/PerformanceMonitor';
import { FCR_SCALE_TABLE } from './lib/utils/constants';
import {
  ProductionDataForm,
  HarvestDetailsForm,
  PricingParametersForm,
  ExpensesForm,
} from './components/forms';
import {
  PerformanceMetrics,
  RevenueBreakdown,
  VATCalculations,
  WithholdingTaxCalculations,
  NetProfitProjections,
} from './components/results';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useWebVitals } from './hooks/useWebVitals';
import { LanguageRedirect } from './components/common/LanguageRedirect';

// Lazy load heavy components
const CycleList = lazy(() => import('./components/comparison/CycleList').then(module => ({ default: module.CycleList })));
const ComparisonTable = lazy(() => import('./components/comparison/ComparisonTable').then(module => ({ default: module.ComparisonTable })));
const ComparisonCharts = lazy(() => import('./components/comparison/ComparisonCharts').then(module => ({ default: module.ComparisonCharts })));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage').then(module => ({ default: module.SettingsPage })));
const BlogList = lazy(() => import('./components/blog/BlogList').then(module => ({ default: module.BlogList })));
const BlogPost = lazy(() => import('./components/blog/BlogPost').then(module => ({ default: module.BlogPost })));

// Loading component
const LoadingSpinner = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <span className="ml-2 text-gray-600 dark:text-gray-400">{t('common.loading')}</span>
    </div>
  );
};

// Language redirect wrapper component
const LanguageRedirectWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <SEOHead />
      <StructuredData />
      <LanguageRedirect />
      {children}
    </>
  );
};

const CalculatorView = () => {
  const { t } = useTranslation();
  const { calculate, isValid } = useCalculationStore();
  const productionData = useProductionStore((state) => state.data);
  const harvestEvents = useProductionStore((state) => state.harvestEvents);
  const pricingParameters = usePricingStore((state) => state.parameters);
  const expenses = useExpenseStore((state) => state.expenses);
  const metrics = useCalculationStore((state) => state.metrics);
  const revenue = useCalculationStore((state) => state.revenue);
  const taxes = useCalculationStore((state) => state.taxes);
  const profit = useCalculationStore((state) => state.profit);
  const resetProduction = useProductionStore((state) => state.reset);
  const resetPricing = usePricingStore((state) => state.reset);
  const resetExpenses = useExpenseStore((state) => state.reset);
  const saveCycle = useCycleStore((state) => state.saveCycle);

  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);

  // Auto-save current session to localStorage
  const sessionData = {
    productionData,
    harvestEvents,
    pricingParameters,
    expenses,
  };
  useLocalStorage('broiler_current_session', sessionData, 500);

  // Keyboard shortcuts will be added after function definitions

  // Auto-calculate when data changes
  useEffect(() => {
    if (isValid()) {
      calculate();
    }
  }, [calculate, isValid]);

  const handleCalculate = () => {
    if (isValid()) {
      calculate();
      // Mobilde hesapla butonuna basınca sonuçlar çekmecesini aç
      setShowResults(true);
      // Giderler accordion'unu kapat
      setExpensesOpen(false);
    } else {
      alert(t('messages.enterAllRequiredFields'));
    }
  };





  const handleNewCycle = () => {
    setShowConfirmClear(true);
  };

  const handleConfirmClear = () => {
    resetProduction();
    resetPricing();
    resetExpenses();
    localStorage.removeItem('broiler_current_session');
    setShowConfirmClear(false);
    alert(t('messages.clearDataSuccess'));
  };

  const handleCancelClear = () => {
    setShowConfirmClear(false);
  };

  const handleSaveCycle = async () => {
    if (!isValid() || !metrics || !revenue || !taxes || !profit) {
      alert(t('common.pleaseCalculateFirst'));
      return;
    }

    const cycleName = prompt(t('messages.enterCycleName'), t('messages.defaultCycleName'));
    if (!cycleName) return;

    try {
      const cycle = {
        id: `cycle_${Date.now()}`,
        timestamp: Date.now(),
        name: cycleName,
        productionData,
        harvestEvents,
        pricingParameters,
        expenses,
        performance: metrics,
        revenue,
        taxes,
        netProfit: profit,
      };

      await saveCycle(cycle);
      alert(t('messages.cycleSavedSuccessfully'));
    } catch (error) {
      alert(t('messages.cycleCouldNotBeSaved') + (error instanceof Error ? error.message : t('messages.unknownError')));
    }
  };

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSave: handleSaveCycle,
    onNewCycle: handleNewCycle,
  });

  return (
    <div className="flex flex-col h-screen">
      <SEOHead />
      <Header
        onSave={handleSaveCycle}
        onNewCycle={handleNewCycle}
        isSaveDisabled={!isValid()}
      />
      
      <div className="flex flex-1 overflow-hidden relative">
        {/* FCR Tablosu Çekmecesi - Üstte (Mobil) - Sadece Tablo */}
        <div className={`
          lg:hidden fixed left-0 right-0 z-40 transition-all duration-300 ease-in-out
          ${showSidebar ? 'top-[60px]' : '-top-full'}
        `}>
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-lg max-h-[60vh] overflow-y-auto max-w-[98%] mx-auto">
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('sidebar.fcrTableTitle')}</h3>
                <button
                  onClick={() => setShowSidebar(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
                >
                  ✕
                </button>
              </div>
              <FCRTable fcrTable={FCR_SCALE_TABLE} />
            </div>
          </div>
        </div>

        {/* Sidebar - Desktop */}
        <div className="hidden lg:block lg:w-64 overflow-y-auto border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="p-4">
            <Sidebar fcrTable={FCR_SCALE_TABLE} />
          </div>
        </div>

        {/* Main Content */}
        <MainContent className="flex-1">
          {/* FCR Tablosu Toggle - Mobil */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="w-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center justify-between hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              <span className="text-blue-700 dark:text-blue-300 font-medium">📊 {t('sidebar.fcrTableToggle')}</span>
              <span className="text-blue-500">{showSidebar ? '▲' : '▼'}</span>
            </button>
          </div>

          {/* Manual FCR Input - Mobil (FCR Tablosu altında) */}
          <div className="lg:hidden mb-4">
            <ManualFCRInput />
          </div>

          <ProductionDataForm />
          <HarvestDetailsForm />
          <PricingParametersForm />
          <ExpensesForm isOpen={expensesOpen} onToggle={setExpensesOpen} />
          
          {/* Calculate Button */}
          <Card>
            <Button
              variant="primary"
              size="lg"
              onClick={handleCalculate}
              fullWidth
            >
              {t('common.calculate')}
            </Button>
          </Card>

          {/* Confirmation Dialog for Clear - Modal Overlay */}
          {showConfirmClear && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
              <Card className="max-w-md w-full">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    ⚠️ {t('confirmDialog.clearDataTitle')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('confirmDialog.clearDataMessage')}
                  </p>
                  <div className="flex space-x-3">
                    <Button
                      variant="danger"
                      onClick={handleConfirmClear}
                      fullWidth
                    >
                      {t('confirmDialog.confirmButton')}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleCancelClear}
                      fullWidth
                    >
                      {t('confirmDialog.cancelButton')}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Sonuçlar Toggle - Mobil */}
          <div className="lg:hidden mt-4">
            <button
              onClick={() => setShowResults(!showResults)}
              className="w-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-center justify-between hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
            >
              <span className="text-green-700 dark:text-green-300 font-medium">📈 {t('sidebar.resultsToggle')}</span>
              <span className="text-green-500">{showResults ? '▲' : '▼'}</span>
            </button>
          </div>
        </MainContent>

        {/* Sonuçlar Çekmecesi - Altta (Mobil) */}
        <div className={`
          lg:hidden fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ease-in-out
          ${showResults ? 'translate-y-0' : 'translate-y-full'}
        `}>
          <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg max-h-[70vh] overflow-y-auto max-w-[98%] mx-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 flex justify-between items-center z-10">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('sidebar.resultsToggle')}</h3>
              <button
                onClick={() => setShowResults(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
              >
                ✕
              </button>
            </div>
            <ResultsPanel>
              <PerformanceMetrics />
              <RevenueBreakdown />
              <VATCalculations />
              <WithholdingTaxCalculations />
              <NetProfitProjections />
            </ResultsPanel>
          </div>
        </div>

        {/* Results Panel - Desktop */}
        <div className="hidden lg:block lg:w-96 overflow-y-auto bg-white dark:bg-gray-800">
          <ResultsPanel>
            <PerformanceMetrics />
            <RevenueBreakdown />
            <VATCalculations />
            <WithholdingTaxCalculations />
            <NetProfitProjections />
          </ResultsPanel>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const ComparisonView = () => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const resetProduction = useProductionStore((state) => state.reset);
  const resetPricing = usePricingStore((state) => state.reset);
  const resetExpenses = useExpenseStore((state) => state.reset);
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const handleNewCycle = () => {
    setShowConfirmClear(true);
  };

  const handleConfirmClear = () => {
    resetProduction();
    resetPricing();
    resetExpenses();
    localStorage.removeItem('broiler_current_session');
    setShowConfirmClear(false);
    alert(t('messages.clearDataSuccess'));
  };

  const handleCancelClear = () => {
    setShowConfirmClear(false);
  };

  return (
    <div className="flex flex-col h-screen">
      <SEOHead title={t('header.comparison')} />
      <Header onNewCycle={handleNewCycle} />
      <MainContent>
        <Suspense fallback={<LoadingSpinner />}>
          <CycleList onSelectionChange={setSelectedIds} />
          {selectedIds.length > 0 && (
            <>
              <ComparisonTable selectedIds={selectedIds} />
              <ComparisonCharts selectedIds={selectedIds} />
            </>
          )}
        </Suspense>
        
        {/* Confirmation Dialog */}
        {showConfirmClear && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <Card className="max-w-md w-full">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  ⚠️ {t('confirmDialog.clearDataTitle')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('confirmDialog.clearDataMessage')}
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="danger"
                    onClick={handleConfirmClear}
                    fullWidth
                  >
                    {t('confirmDialog.confirmButton')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelClear}
                    fullWidth
                  >
                    {t('confirmDialog.cancelButton')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </MainContent>
      <Footer />
    </div>
  );
};

const SettingsView = () => {
  const { t } = useTranslation();
  const resetProduction = useProductionStore((state) => state.reset);
  const resetPricing = usePricingStore((state) => state.reset);
  const resetExpenses = useExpenseStore((state) => state.reset);
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const handleNewCycle = () => {
    setShowConfirmClear(true);
  };

  const handleConfirmClear = () => {
    resetProduction();
    resetPricing();
    resetExpenses();
    localStorage.removeItem('broiler_current_session');
    setShowConfirmClear(false);
    alert(t('messages.clearDataSuccess'));
  };

  const handleCancelClear = () => {
    setShowConfirmClear(false);
  };

  return (
    <div className="flex flex-col h-screen">
      <SEOHead title={t('header.settings')} />
      <Header onNewCycle={handleNewCycle} />
      <MainContent>
        <Suspense fallback={<LoadingSpinner />}>
          <SettingsPage />
        </Suspense>
        
        {/* Confirmation Dialog */}
        {showConfirmClear && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <Card className="max-w-md w-full">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  ⚠️ {t('confirmDialog.clearDataTitle')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('confirmDialog.clearDataMessage')}
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="danger"
                    onClick={handleConfirmClear}
                    fullWidth
                  >
                    {t('confirmDialog.confirmButton')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelClear}
                    fullWidth
                  >
                    {t('confirmDialog.cancelButton')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </MainContent>
      <Footer />
    </div>
  );
};

const BlogListView = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-screen">
      <SEOHead title={t('blog.title')} />
      <Header />
      <MainContent>
        <Suspense fallback={<LoadingSpinner />}>
          <BlogList />
        </Suspense>
      </MainContent>
      <Footer />
    </div>
  );
};

const BlogPostView = () => {
  return (
    <div className="flex flex-col h-screen">
      <Header />
      <MainContent>
        <Suspense fallback={<LoadingSpinner />}>
          <BlogPost />
        </Suspense>
      </MainContent>
      <Footer />
    </div>
  );
};

function App() {
  const theme = useUIStore((state) => state.theme);
  
  // Initialize Web Vitals tracking
  useWebVitals();

  return (
    <div className={theme}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <BrowserRouter>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Dil kodu olmayan root path - otomatik yönlendirme */}
              <Route path="/" element={<LanguageRedirectWrapper><CalculatorView /></LanguageRedirectWrapper>} />
              <Route path="/comparison" element={<LanguageRedirectWrapper><ComparisonView /></LanguageRedirectWrapper>} />
              <Route path="/settings" element={<LanguageRedirectWrapper><SettingsView /></LanguageRedirectWrapper>} />
              <Route path="/blog" element={<LanguageRedirectWrapper><BlogListView /></LanguageRedirectWrapper>} />
              <Route path="/blog/:slug" element={<LanguageRedirectWrapper><BlogPostView /></LanguageRedirectWrapper>} />
              
              {/* Türkçe dil kodlu path'ler */}
              <Route path="/tr" element={<CalculatorView />} />
              <Route path="/tr/comparison" element={<ComparisonView />} />
              <Route path="/tr/settings" element={<SettingsView />} />
              <Route path="/tr/blog" element={<BlogListView />} />
              <Route path="/tr/blog/:slug" element={<BlogPostView />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        
        {/* Performance Monitor - Development only */}
        <PerformanceMonitor />
      </div>
    </div>
  );
}

export default App;
